/*
 * Migration Lot 3 (2026-04-30) : `AvatarMui` est désormais bâti sur
 * shadcn/ui (Radix Avatar) au lieu de `@mui/material/Avatar`. L'API
 * publique `{ link, text, ...rest }` est préservée. Le coloriage
 * déterministe à partir du nom (`stringToColor`) est conservé tel quel
 * — c'est un asset visuel utilisé pour distinguer les utilisateurs sans
 * photo.
 *
 * Bug latent corrigé : l'ancien code crashait sur un `text` vide ou
 * sans espace (`name.split(" ")[0][0]` → undefined.0). On défend avec
 * de l'optional chaining — comportement identique pour les entrées
 * valides, plus de crash sur les bords.
 *
 * 6 consommateurs : Layout/DashboardWrapper, Patient, PersonnelMedecin,
 * Profile, Users (index + ShowUser).
 */
import * as React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

function stringToColor(string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringInitials(name) {
  const parts = (name ?? "").trim().split(/\s+/);
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
}

export default function AvatarMui({ link, text, ...rest }) {
  const initials = stringInitials(text || "");
  const bg = stringToColor(text || "");

  return (
    <Avatar {...rest}>
      {/*
        AvatarImage déclenche le chargement de l'image ; en cas d'erreur
        ou de src vide, Radix bascule sur le fallback. Reproduction
        fidèle du comportement MUI (qui a la même logique interne).
      */}
      <AvatarImage src={link} alt={text} />
      {/*
        Couleur de fond dynamique en inline style — bat le `bg-muted` par
        défaut de shadcn AvatarFallback. Texte blanc pour lisibilité sur
        une couleur arbitraire (mêmes choix que l'ancien wrapper MUI qui
        s'appuyait sur `theme.palette.getContrastText`).
      */}
      <AvatarFallback style={{ backgroundColor: bg, color: "white" }}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
